/* istanbul ignore file */
import {
  Banner,
  Box,
  Button,
  Flex,
  Loader,
  Pagination,
  Table,
  Tbody,
  Td,
  Text,
  TextField,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@kandji-inc/nectar-ui';
import type { TPlatform } from '@shared/types/platform.types';
import React, { useMemo, useState } from 'react';
import ApplicationBlocklistModal from 'src/app/components/interface/modals/ApplicationBlocklist';
import { usePagination } from 'src/components/ui';
import { noResultsAlt } from 'src/features/blueprint-flow/assets';
import Empty from 'src/features/device-status-tab/empty';
import useAdjustSidebarChatBubble from 'src/features/util/hooks/use-adjust-sidebar-chat-bubble';
import { get } from 'src/features/util/lib';
import { useDebouncedState } from 'src/hooks/useDebouncedState';
import { useGetSidebarWidth } from 'src/hooks/useGetSidebarWidth';
import useSort, { getSortParam } from 'src/hooks/useSort';
import { i18n } from 'src/i18n';
import { highlightedText } from 'src/utils/HighlightTextUtils';
import { getDevicePlatform } from 'src/utils/PlatformUtils';
import {
  EmptyApplications,
  ExpandedRow,
  iOsColumns,
  macColumns,
  uniquePlatformKey,
  windowsColumns,
} from './common';
import { useGetAppleApplicationInfo } from './hooks/useGetAppleApplicationInfo';
import { useGetAppleApplications } from './hooks/useGetAppleApplications';
import { useGetWindowsApplications } from './hooks/useGetWindowsApplications';
import { useUpdateAppleApplicationInfo } from './hooks/useUpdateAppleApplicationInfo';

interface DeviceApplicationsListProps {
  computer: any;
}

const getTableColumns = (platform: TPlatform, deviceFamily?: string) => {
  if (platform === 'Windows') {
    return windowsColumns();
  }

  if (platform === 'Apple') {
    return deviceFamily === 'Mac' ? macColumns() : iOsColumns();
  }

  return [];
};

export default function DeviceApplicationsList(
  props: DeviceApplicationsListProps,
) {
  useAdjustSidebarChatBubble();

  const { computer } = props;
  const { id: deviceId, device_family: deviceFamily } = computer;
  const devicePlatform = getDevicePlatform(deviceFamily);
  const isApplePlatform = devicePlatform === 'Apple';
  const width = useGetSidebarWidth();

  const [blockAppModal, setBlockAppModal] = useState({
    data: null,
    isOpen: false,
  });
  const [sort, onSortColumn] = useSort({
    id: null,
    state: 'asc',
  });
  const [expandedRows, setExpandedRows] = useState([]);
  const [debouncedTerm, setTerm, term] = useDebouncedState<string>('');

  const {
    paginationState: { pageIndex, pageSize },
    setPagination,
  } = usePagination({
    defaultPageSize: 25,
  });
  const page = pageIndex + 1;

  const { mutateAsync: updateApplicationInfo, isPending: isUpdatingAppInfo } =
    useUpdateAppleApplicationInfo(deviceId);
  const { data: infoRes } = useGetAppleApplicationInfo(
    deviceId,
    isApplePlatform,
  );
  const {
    isFetched: isAppleApplicationsFetched,
    data: appsRes,
    isLoading: isAppleLoading,
  } = useGetAppleApplications({
    deviceId,
    deviceFamily,
    params: {
      page,
      sizePerPage: pageSize,
      ordering: getSortParam(sort.id, sort.state),
      search: debouncedTerm,
    },
    enabled: isApplePlatform,
  });
  const {
    data: windowsAppsRes,
    isFetched: isWindowsApplicationsFetched,
    isLoading: isWindowsLoading,
  } = useGetWindowsApplications({
    deviceId,
    params: {
      page,
      per_page: pageSize,
      order_by: getSortParam(sort.id, sort.state),
      search: debouncedTerm,
    },
    enabled: !isApplePlatform,
  });

  const isApplicationsFetched =
    isWindowsApplicationsFetched || isAppleApplicationsFetched;
  const isLoadingApplications = isWindowsLoading || isAppleLoading;
  const infoFetchedAt =
    infoRes?.created_at__max ||
    windowsAppsRes?.last_sync_at ||
    (isApplePlatform && deviceFamily !== 'Mac'
      ? computer.mdm_applications_updated_at
      : null);
  const appResults = appsRes?.results || windowsAppsRes?.results || [];
  const totalCount = appsRes?.count || windowsAppsRes?.count || 0;
  const totalPages = Math.ceil((totalCount || 0) / pageSize);
  const hasFilterActive = Boolean(debouncedTerm);
  const hasResults = Boolean(appResults.length);
  const shouldShowEmptyState =
    !isLoadingApplications && !hasFilterActive && totalCount === 0;
  const columns = getTableColumns(devicePlatform, deviceFamily);

  const TableRows = useMemo((): JSX.Element => {
    if (hasFilterActive && !hasResults) {
      return (
        <Tr hoverBg="none" css={{ height: '350px' }}>
          <Td
            // @ts-ignore typing
            valign="top"
            colSpan={columns.length}
            css={{
              $$tdActiveBg: 'transparent',
              color: '$neutral80',
            }}
          >
            <Flex alignItems="center">
              <Empty
                image={noResultsAlt}
                title={i18n.t('No results found')}
                message={i18n.t(
                  "We couldn't find a match. Try searching with different keywords.",
                )}
                css={{ height: 'inherit' }}
              />
            </Flex>
          </Td>
        </Tr>
      );
    }
    return appResults?.map((app) => {
      const id = app[uniquePlatformKey[devicePlatform]];
      const canExpand = devicePlatform === 'Apple';
      const isExpanded = expandedRows.includes(id);

      return (
        <>
          <Tr
            key={id}
            css={{ cursor: canExpand ? 'pointer' : 'inherit' }}
            onClick={() => {
              if (canExpand) {
                setExpandedRows(
                  isExpanded
                    ? expandedRows.filter((row) => row !== id)
                    : [...expandedRows, id],
                );
              }
            }}
          >
            {columns.map((column) => {
              const value = get<string | any>(app, column.dataKey);
              const Cell = column.cell;
              const expandedCss = isExpanded ? { boxShadow: 'none' } : {};

              return (
                <Td
                  key={column.name}
                  title={value}
                  css={{
                    $$tdActiveBg: 'transparent',
                    ...expandedCss,
                    ...(column.meta?.row?.css || {}),
                  }}
                >
                  {Cell ? (
                    <Cell
                      key={column.name}
                      application={app}
                      isExpanded={isExpanded}
                      onBlockApplication={() =>
                        setBlockAppModal({
                          data: { ...app, blueprintId: computer.blueprint_id },
                          isOpen: true,
                        })
                      }
                    />
                  ) : (
                    highlightedText(`${value}` || '-', debouncedTerm)
                  )}
                </Td>
              );
            })}
          </Tr>
          {isExpanded && (
            <Tr key={`${id}_expandedContent`} hoverBg="none">
              <Td
                title="expand toggle"
                css={{
                  $$tdActiveBg: 'transparent',
                }}
              >
                {null}
              </Td>
              <Td
                title="Expanded content"
                // @ts-ignore typing
                valign="top"
                colSpan={columns.length - 1}
                css={{
                  $$tdActiveBg: 'transparent',
                  color: '$neutral80',
                }}
              >
                <ExpandedRow
                  application={app}
                  devicePlatform={devicePlatform}
                  deviceFamily={deviceFamily}
                />
              </Td>
            </Tr>
          )}
        </>
      );
    });
  }, [appResults, expandedRows]);

  return (
    <Box hFull css={{ marginLeft: '-24px' }}>
      <Flex flow="column" gap="md" py3 px5>
        {isApplePlatform && deviceFamily !== 'Mac' && (
          <Banner
            text={i18n.t(
              'This lists third-party apps installed on this device. Built-in apps from Apple are not inventoried.',
            )}
            css={{ alignSelf: 'flex-start' }}
          />
        )}
        <Flex justifyContent="space-between" alignItems="center">
          <Box>
            {!shouldShowEmptyState && !isLoadingApplications && (
              <TextField
                compact
                placeholder={i18n.t('Search applications')}
                icon="magnifying-glass"
                value={term}
                showClearButton={Boolean(term)}
                onClear={() => setTerm('')}
                onChange={(e) => setTerm(e.target.value)}
                css={{ minWidth: '240px' }}
              />
            )}
          </Box>
          {infoFetchedAt && (
            <Flex gap="xs" alignItems="center">
              <Text>{i18n.t('Last Update:')} </Text>
              <Text css={{ fontWeight: 500 }}>
                {i18n.format.datetime(infoFetchedAt)}
              </Text>
              {isApplePlatform && deviceFamily !== 'Mac' && (
                <Tooltip
                  content={i18n.t('{text}', {
                    text: isUpdatingAppInfo ? 'Updating' : 'Update now',
                  })}
                  css={{ zIndex: 20 }}
                >
                  <Button
                    disabled={isUpdatingAppInfo}
                    variant="subtle"
                    icon={{ name: 'arrows-rotate' }}
                    compact
                    onClick={(e) => {
                      e.stopPropagation();
                      updateApplicationInfo({
                        toUpdate: [
                          'InstalledApplicationList',
                          'ManagedApplicationConfiguration',
                        ],
                      });
                    }}
                  />
                </Tooltip>
              )}
            </Flex>
          )}
        </Flex>

        {shouldShowEmptyState && <EmptyApplications />}
        {isLoadingApplications && !shouldShowEmptyState && (
          <Flex alignItems="center" justifyContent="center">
            <Loader />
          </Flex>
        )}
        {!isLoadingApplications && !shouldShowEmptyState && (
          <Box css={{ overflow: 'scroll' }}>
            <Table aria-label="apps-table">
              <Thead
                style={{
                  position: 'sticky',
                  top: 0,
                }}
              >
                <Tr>
                  {columns.map((column) => {
                    const isSortColumn = sort.id === column.sortKey;
                    const state = isSortColumn ? sort.state : 'none';
                    const onSort = () => {
                      setExpandedRows([]);
                      isSortColumn
                        ? onSortColumn()
                        : onSortColumn(column.sortKey, 'asc');
                    };

                    return (
                      <Th
                        key={column.name}
                        title={null}
                        sort={
                          column.isSortable && {
                            state,
                            onSort,
                          }
                        }
                        css={column.meta?.header?.css}
                        showMenu
                      >
                        {column.name}
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>
              <Tbody>{TableRows}</Tbody>
            </Table>
          </Box>
        )}
      </Flex>
      {!shouldShowEmptyState && (
        <Box
          css={{
            position: 'fixed',
            bottom: 0,
            padding: '$3 $5',
            borderTop: '1px solid $neutral20',
            width: width ? `calc(100% - ${width}px)` : '100%',
            backgroundColor: '$neutral0',
          }}
        >
          <Pagination
            menuAbove
            currentPage={page}
            totalPages={totalPages || 1}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            onPageChange={(page) =>
              setPagination((prev) => ({
                ...prev,
                pageIndex: page - 1,
              }))
            }
          />
        </Box>
      )}
      {blockAppModal.isOpen && (
        <ApplicationBlocklistModal
          onHide={() => setBlockAppModal({ data: null, isOpen: false })}
          info={blockAppModal.data}
        />
      )}
    </Box>
  );
}
